import { action, observable, reaction, computed, runInAction } from "mobx";
import { ProductEditForm } from "application/product/forms";
import { BaseEditViewStore, BaasicDropdownStore } from "core/stores";
import { applicationContext } from "core/utils";
import { LookupService } from "common/services";
import {
    ProductService,
    UnitOfMeasureService,
    SubCategoryService,
    ProcedureCodeService,
} from "application/product/services";
import { ProductTagService } from 'application/product-tag/services';
import { ProductTypeService } from 'application/product-type/services';
import _ from "lodash";
import { ProductTypeTagService } from "application/product-type/services";

@applicationContext
class ProductEditViewStore extends BaseEditViewStore {
    @observable accessor isEnteralItemSelected = false;
    @observable accessor showInnerCannulaDropdown = false;
    @observable accessor showPumpBrand = false;
    @observable accessor showProductType = false;
    @observable accessor showSpecialtyType = false;
    @observable accessor isWoundCareItemSelected = false;
    @observable accessor woundCareItemCategoryId = null;
    @observable accessor showProductTags = false; 
    @observable accessor allowedTagIds = []; 
    @observable accessor showVendor = false;

    constructor(rootStore) {
        const id = rootStore.routerStore.routerState.params.id;
        const service = new ProductService(
            rootStore.application.baasic.apiClient
        );
        const unitOfMeasureService = new UnitOfMeasureService(
            rootStore.application.baasic.apiClient
        );
        const subCategoryService = new SubCategoryService(
            rootStore.application.baasic.apiClient
        );
        const lookupService = new LookupService(
            rootStore.application.baasic.apiClient
        );
        const procedureCodeService = new ProcedureCodeService(
            rootStore.application.baasic.apiClient
        );
        const productTagService = new ProductTagService(rootStore.application.baasic.apiClient);
        const productTypeService = new ProductTypeService(
            rootStore.application.baasic.apiClient
        );
        const productTypeTagService = new ProductTypeTagService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            name: "role",
            id: id,
            Form: ProductEditForm,
            authorization: "portalSection.update",
            actions: () => {
                return {
                    get: async (id) => {
                        const response = await service.get(id, {
                            embed: "ItemType,InnerCannula,DefaultItemType,ProductProductTags,PumpBrand,ItemCategory,SubCategory,ProcedureCode_,ParentProduct",
                        });
                        if (response.data.innerCannula) {
                            this.innerCannulaStore.setValue(
                                response.data.innerCannula
                            );
                        }
                        if (response.data.parentProduct) {
                            this.parentProductStore.setValue(
                                response.data.parentProduct
                            );
                        }
                        this.itemTypeStore.setValue(response.data.itemType);
                        this.vendorStore.setValue(response.data.vendor)
                        this.productTypeStore.setValue(
                            response.data.defaultItemType
                        );

                        if (response.data.defaultItemType){
                            this.defaultItemTypeId = response.data.defaultItemType.id;
                            this.tagsStore.setValue(
                                (response.data.productProductTags && Array.isArray(response.data.productProductTags) 
                                    ? response.data.productProductTags.map((item) => ({
                                        id: item.productTag && item.productTag.id,
                                        name: item.productTag && item.productTag.name
                                    })) : []
                                )
                            );
                        }
                        
                        this.pumpBrandStore.setValue(response.data.pumpBrand);
                        this.procedureCodeStore.setValue(
                            response.data.procedureCode_
                        );
                        response.data.isActive = !response.data.isActive;
                        return response.data;
                    },
                    update: async (resource) => {
                        resource.isActive = !resource.isActive;
                        if (resource.productTagIds && this.showProductTags) {
                            resource.productTagIds = resource.productTagIds.map(tag => tag.id);
                        }

                        const response = await service.update(resource);
                        return response.data;
                    },
                    create: async (resource) => {

                        if (resource.productTagIds && this.showProductTags) {
                            resource.productTagIds = resource.productTagIds.map(tag => tag.id);
                        }

                        const response = await service.create(resource);
                        return response.data;
                    },
                };
                
            },
            
            FormClass: ProductEditForm,
        });

        reaction(
            () => this.isDiscontinued, 
            (discontinued) => {
                if (discontinued) {
                    this.form.set({ backordered: false });
                }
            }
        )
        this.productTypeService = productTypeService;
        this.subCategoryService = subCategoryService;
        this.productTypeTagService = productTypeTagService;

        this.unitOfMeasureStore = new BaasicDropdownStore(
            {
                filterable: true,
            },
            {
                fetchFunc: async (term) => {
                    const response = await unitOfMeasureService.find({
                        searchQuery: term,
                        isActive: true,
                        rpp: 50,
                    });

                    return response.data.item;
                },
                onChange: (unitOfMeasureId) => {
                    this.form.set({ unitOfMeasureId: unitOfMeasureId });
                },
            }
        );

        this.categoryStore = new BaasicDropdownStore(
            {
                filterable: true,
                initFetch: false,
            },
            {
                fetchFunc: async () => {
                    const response = await lookupService.getLookups(
                        "ItemCategory"
                    );

                    runInAction(() => {
                        this.showProductType =
                            this.categoryStore.value &&
                            (
                                this.categoryStore.value.abrv === "enteral" ||
                                this.categoryStore.value.abrv === "ostomy" ||
                                this.categoryStore.value.abrv === "respiratory" ||
                                this.categoryStore.value.abrv === "urological"
                            );
                    });

                    if (this.showProductType) {
                        await this.productTypeStore.filterAsync();
                    }
                
                    return response.itemCategory.filter(
                        (c) =>
                            c.abrv === "enteral" ||
                            c.abrv === "respiratory" ||
                            c.abrv === "urological" ||
                            c.abrv === "ostomy" ||
                            c.abrv === "wound-care" ||
                            c.abrv === "no-item-category" ||
                            c.abrv === "pumps"
                    );
                },
                onChange: async (itemCategoryId) => {
                    this.form.set({ itemCategoryId: itemCategoryId });

                    this.showInnerCannulaDropdown = false;
                    this.showPumpBrand = false;
                    this.showProductType = false;
                    this.showSpecialtyType = false;
                    this.showVendor = false;

                    this.productTypeStore.setValue(null);
                    this.form.set({ defaultItemTypeId: null });
                    await this.tagsStore.filterAsync();

                    this.innerCannulaStore.setValue(null);
                    this.form.set({ innerCannulaId: null });

                    this.pumpBrandStore.setValue(null);
                    this.form.set({ pumpBrandId: null });

                    this.form.set({ subCategoryId: null });
                    this.subCategoryStore.setValue(null);
                    await this.subCategoryStore.filterAsync();

                    this.specialtyTypeStore.setValue(null);
                    this.form.set({ specialtyTypeId: null });

                    this.showProductType =
                        this.categoryStore.value &&
                        (
                            this.categoryStore.value.abrv === "enteral" ||
                            this.categoryStore.value.abrv === "ostomy" ||
                            this.categoryStore.value.abrv === "respiratory" ||
                            this.categoryStore.value.abrv === "urological"
                        );
                    
                    this.clearProductTagDropdown();
                    if (this.showProductType) {
                        await this.productTypeStore.filterAsync();
                    } else {
                        this.productTypeStore.setValue(null);
                        this.form.set({ defaultItemTypeId: null });
                    }

                   this.updateVendorVisibility();

                    if (!this.showVendor) {
                        this.vendorStore.setValue(null);
                        this.form.set({ vendorId: null });
                    }

                    this.isEnteralItemSelected =
                        this.isEnteralItem(itemCategoryId);

                    this.isWoundCareItemSelected = 
                        this.isWoundCareItem(itemCategoryId);
                },
            }
        );

        this.subCategoryStore = new BaasicDropdownStore(
            {
                filterable: true,
                initFetch: false,
            },
            {
                fetchFunc: async (term) => {
                    if (this.categoryStore.value == null) {
                        return [];
                    }

                    const response = await subCategoryService.find({
                        searchQuery: term,
                        itemCategoryId: this.categoryStore.value.id,
                        rpp: 50,
                    });
                    this.showSpecialtyType =
                        this.categoryStore.value.abrv === "enteral" ||
                        this.categoryStore.value.abrv === "urological";

                    if (this.showSpecialtyType) {
                        await this.specialtyTypeStore.filterAsync();
                    }
                    if (this.categoryStore.value.abrv === "enteral") {
                        return response.data.item.filter(
                            (i) =>
                                i.abrv === "Enteral" ||
                                i.abrv === "G-Tube" ||
                                i.abrv === "Oral-Supplements"
                        );
                    } else {
                        if (response.data.item.length === 1) {
                            this.subCategoryStore.setValue(
                                response.data.item[0]
                            );
                            this.form.set({
                                subCategoryId: response.data.item[0].id,
                            });

                            this.showInnerCannulaDropdown =
                                this.subCategoryStore.value.abrv ===
                                "Trach-Tube";
                            this.showPumpBrand =
                                this.subCategoryStore.value.abrv === "Enteral";
                      
                            if (this.showProductType) {
                                await this.productTypeStore.filterAsync();
                            }
                        }
                        return response.data.item;
                    }
                },
                onChange: async () => {
                    this.showInnerCannulaDropdown =
                        this.subCategoryStore.value &&
                        this.subCategoryStore.value.abrv === "Trach-Tube";
                    this.showPumpBrand =
                        this.subCategoryStore.value &&
                        this.subCategoryStore.value.abrv === "Enteral";
              
                    if (!this.showInnerCannulaDropdown) {
                        this.innerCannulaStore.setValue(null);
                        this.form.set({ innerCannulaId: null });
                    }

                    if (!this.showPumpBrand) {
                        this.pumpBrandStore.setValue(null);
                        this.form.set({ pumpBrandId: null });
                    }
                },
            }
        );

        this.itemTypeStore = new BaasicDropdownStore(
            {
                filterable: false,
            },
            {
                fetchFunc: async () => {
                    const response = await lookupService.getLookups("ItemType");
                    return response.itemType;
                },
            }
        );

        this.vendorStore = new BaasicDropdownStore(
        {
            filterable: false,
        },
        {
            fetchFunc: async () => {
                const response = await lookupService.getLookups("Vendor");
                const vendorsSorted = _.orderBy(response.vendor, ["name"], ["asc"]);
                return vendorsSorted;
            },
            onChange: (vendorId) => {
                this.form.set({ vendorId });
            }
        }
        );

        this.productTypeStore = new BaasicDropdownStore(
            {
                filterable: false,
                initFetch: false,
            },
            {
                fetchFunc: async () => {
                    const response = await lookupService.getLookups(
                        "DefaultItemType"
                    );
                   
                    const currentCategoryId = this.categoryStore.value.id;
                    const productType = response.defaultItemType.filter((i) =>
                        i.itemCategoryId === currentCategoryId
                    );
                    return productType.sort((a, b) => a.name.localeCompare(b.name));
                },
                onChange: async (typeId) => {
                    this.form.set({ defaultItemTypeId: typeId });
                    if (!typeId) {
                        this.showProductTags = false;
                    } else {
                        this.showProductTags = true;
                    }

                    this.updateVendorVisibility();
                       
                    await this.setProductTagDropdownItems(typeId);
                }
            }
        );

        (async () => { this.allProductTags = (await productTagService.find({ rpp: 100})).data.item; })();
        this.tagsStore = new BaasicDropdownStore(
            {
                filterable: true,
                multi: true,
            },
            {
                fetchFunc: async () => {
                    const data = await this.setProductTagDropdownItems(this.defaultItemTypeId);
                    if (data){
                        return data.map((item) => ({
                            id: item.id,
                            name: item.name
                        }))
                    }
                },
            }
        );

        this.procedureCodeStore = new BaasicDropdownStore(
            {
                filterable: true,
            },
            {
                fetchFunc: async (term) => {
                    const response = await procedureCodeService.find({
                        searchQuery: term,
                        rpp: 25,
                    });
                    return response.data.item;
                },
            }
        );

        this.pumpBrandStore = new BaasicDropdownStore(
            {
                filterable: false,
            },
            {
                fetchFunc: async () => {
                    const response = await lookupService.getLookups(
                        "PumpBrand"
                    );
                    let pumpBrand = _.orderBy(
                        response.pumpBrand,
                        ["name"],
                        ["asc"]
                    );
                    return pumpBrand;
                },
            }
        );

        this.innerCannulaStore = new BaasicDropdownStore(
            {
                filterable: true,
                textField: "itemId",
            },
            {
                fetchFunc: async (term) => {
                    const response = await service.find({
                        searchQuery: term,
                        rpp: 50,
                        sort: "itemId|asc",
                        isActive: true,
                        productToInclude: id,
                    });
                    return response.data.item;
                },
            }
        );

        this.specialtyTypeStore = new BaasicDropdownStore(
            {
                initFetch: false,
            },
            {
                fetchFunc: async () => {
                    const response = await lookupService.getLookups(
                        "SpecialtyType"
                    );
                    return _.orderBy(
                        response.specialtyType.filter(
                            (c) =>
                                c.itemCategoryId ===
                                    this.categoryStore.value.id &&
                                (c.abrv === "diabetic-formula" ||
                                    c.abrv === "elemental-formula" ||
                                    c.abrv === "pulmonary-formula" ||
                                    c.abrv === "protein-supplement" ||
                                    c.abrv === "pump" ||
                                    c.abrv === "silicone-catheter" ||
                                    c.abrv === "coude-catheter" ||
                                    c.abrv === "3-way-catheter" ||
                                    c.abrv === "renal-formula-nepro-nr" ||
                                    c.abrv === "renal-formula-suplena")
                        ),
                        ["name"],
                        ["asc"]
                    );
                },
            }
        );

        this.parentProductStore = new BaasicDropdownStore(
            {
                filterable: true,
                textField: "itemId",
            },
            {
                fetchFunc: async (term) => {
                    const response = await service.find({
                        searchQuery: term,
                        rpp: 50,
                        sort: "itemId|asc",
                        isActive: true,
                    });
                    return response.data.item.filter((i) => i.id != this.id);
                },
            }
        );

        this.widthMeasurementUnitStore = new BaasicDropdownStore(
            {
                filterable: false,
            },
            {
                fetchFunc: async () => {
                    const response = await lookupService.getLookups(
                        "measurementunit"
                    );
                    return response["measurementUnit"].map((item) => ({
                        id: item.id,
                        abrv: item.abrv,
                        name: item.name,
                    }));
                },
            }
        );

        this.heightMeasurementUnitStore = new BaasicDropdownStore(
            {
                filterable: false,
            },
            {
                fetchFunc: async () => {
                    const response = await lookupService.getLookups(
                        "measurementunit"
                    );
                    return response["measurementUnit"].map((item) => ({
                        id: item.id,
                        abrv: item.abrv,
                        name: item.name,
                    }));
                },
            }
        );

        this.woundCareItemSubcategoryStore = new BaasicDropdownStore(
            {
                filterable: false
            },
            {
                fetchFunc: async () => {
                    const response = await lookupService.getLookups(
                        "woundcareitemsubcategory"
                    );
                    return response["woundCareItemSubCategory"].map((item) => ({
                        id: item.id,
                        abrv: item.abrv,
                        name: item.name
                    }))
                },
            }
        );
    }

    @action
    async initialize() {
        await this.getEnteralItemCategories();

        await this.getWoundCareItemCategory();

        if (this.isEdit) {
            await this.getResource(this.id);
            this.isEnteralItemSelected = this.isEnteralItem(
                this.item.itemCategoryId
            );
            this.isWoundCareItemSelected = this.isWoundCareItem(
                this.item.itemCategoryId
            );

            this.categoryStore.setValue(this.item.itemCategory);
            this.subCategoryStore.setValue(this.item.subCategory);
            this.tagsStore.setValue(this.item.name)

            await this.categoryStore.filterAsync();
            await this.subCategoryStore.filterAsync();
            await this.productTypeStore.filterAsync();
            await this.specialtyTypeStore.filterAsync();
            await this.tagsStore.filterAsync();

            this.tagsStore.setValue(
            (this.item.productProductTags || []).map(pp => ({
                id: pp.productTag.id,
                name: pp.productTag.name
            }))
            );
            
            this.showInnerCannulaDropdown =
                this.item.subCategory &&
                this.item.subCategory.abrv === "Trach-Tube";
            this.showPumpBrand =
                this.item.subCategory &&
                this.item.subCategory.abrv === "Enteral";
            this.showProductType =
                this.categoryStore.value &&
                    (
                        this.categoryStore.value.abrv === "enteral" ||
                        this.categoryStore.value.abrv === "ostomy" ||
                        this.categoryStore.value.abrv === "respiratory" ||
                        this.categoryStore.value.abrv === "urological"
                    );
            this.showSpecialtyType =
                this.item.itemCategory.abrv === "enteral" ||
                this.item.itemCategory.abrv === "urological";

            this.updateVendorVisibility();
        } else {
            await this.categoryStore.filterAsync();
            this.form.clear();
            this.form.$('units').value = 1;
        }
    }

    @action.bound
    async getEnteralItemCategories() {
        const lookupService = new LookupService(
            this.rootStore.application.baasic.apiClient
        );
        const itemCategoryLookup = await lookupService.getLookups(
            "ItemCategory"
        );
        let itemCategory = itemCategoryLookup.itemCategory.filter((i) =>
            i.abrv.includes("enteral")
        );

        this.enteralItemCategories = _.orderBy(itemCategory, ["name"], ["asc"]);
    }

    @action.bound
    async getWoundCareItemCategory() {
        const lookupService = new LookupService(
            this.rootStore.application.baasic.apiClient
        );
        const itemCategoryLookup = await lookupService.getLookups(
            "ItemCategory"
        );
        let woundCareItemCategory = itemCategoryLookup.itemCategory.find((i) =>
            i.abrv === "wound-care"
        );
        this.woundCareItemCategoryId = woundCareItemCategory.id;
    }

    @action.bound
    isEnteralItem(itemCategoryId) {
        return this.enteralItemCategories.some((i) => i.id === itemCategoryId);
    }

    @action.bound
    isWoundCareItem(itemCategoryId) {
        return itemCategoryId === this.woundCareItemCategoryId;
    }

    @action.bound
    async onInit({ initialLoad }) {
        if (!initialLoad) {
            this.rootStore.routerStore.goBack();
        }
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            const tempUom = resource.unitOfMeasureId
                ? this.unitOfMeasureStore.originalItems.find(
                      (i) => i.id === resource.unitOfMeasureId
                  )
                : null;

            if (this.isEnteralItemSelected) {
                await this.actions.update({
                    id: this.id,
                    ...resource,
                    uom: tempUom !== null ? tempUom.name : "",
                });
            } else {
                await this.actions.update({
                    id: this.id,
                    ...resource,
                    calsPerMl: 0,
                    caloriesPerContainer: 0,
                    containerSize: "",
                    itemTypeId: null,
                    uom: tempUom !== null ? tempUom.name : "",
                });
            }

            this.form.setFieldsDisabled(false);

            await this.rootStore.routerStore.goBack();
            await setTimeout(
                () =>
                    this.rootStore.notificationStore.success(
                        "PRODUCT.EDIT.SUCCESS_UPDATE"
                    ),
                10
            );
        } catch (err) {
            this.form.setFieldsDisabled(false);

            if (err && err.data) {
                this.rootStore.notificationStore.error(err.data);
            } else {
                this.rootStore.notificationStore.error(`A product with this Item ID already exists.`);
            }
        }
    }

    @computed
    get isDiscontinued() {
        return this.form.$("isActive").value;
    }

    @action.bound
    async createResource(resource) {
        if (!this.actions.create) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            const tempUom = resource.unitOfMeasureId
                ? this.unitOfMeasureStore.originalItems.find(
                      (i) => i.id === resource.unitOfMeasureId
                  )
                : null;

            if (this.isEnteralItemSelected) {
                await this.actions.create({
                    ...resource,
                    uom: tempUom !== null ? tempUom.name : "",
                });
            } else {
                await this.actions.create({
                    ...resource,
                    calsPerMl: 0,
                    caloriesPerContainer: 0,
                    containerSize: "",
                    uom: tempUom !== null ? tempUom.name : "",
                });
            }

            this.form.setFieldsDisabled(false);

            await this.rootStore.routerStore.goBack();
            await setTimeout(
                () =>
                    this.rootStore.notificationStore.success(
                        "PRODUCT.EDIT.SUCCESS_CREATE"
                    ),
                10
            );
        } catch (err) {
            this.form.setFieldsDisabled(false);

            if (err && err.data) {
                this.rootStore.notificationStore.error(err.data);
            } else {
                this.rootStore.notificationStore.error(
                    `A product with this Item ID already exists.`
                );
            }
        }
    }

    @action.bound
    goToProductHistory() {
        window.open(
            `${
                this.rootStore.routerStore.findRoute(
                    "master.app.main.product.product-history.list"
                ).pattern
            }?productId=${this.id}`
        );
    }

    @action.bound
    async setProductTagDropdownItems(productTypeId) {
        if (!productTypeId) return null;
        this.clearProductTagDropdown();
        let tags = await this.productTypeTagService.find({defaultItemTypeId: productTypeId});

        if (tags.data.item.length !== 0){
            let filtertedTags = this.allProductTags.filter(t => tags.data.item.some(t2 => t2.productTagId === t.id));
            this.tagsStore.setItems(filtertedTags.map((item) => ({
                id: item.id,
                name: item.name
            })));
            
            this.showProductTags = true;
            return filtertedTags
        }
    }

    @action.bound
    updateVendorVisibility() {
        this.showVendor =
            this.categoryStore.value &&
            (
            this.categoryStore.value.abrv === 'enteral'    ||
            this.categoryStore.value.abrv === 'ostomy'     ||
            this.categoryStore.value.abrv === 'respiratory'||
            this.categoryStore.value.abrv === 'urological'
            ) &&
            (
            !this.productTypeStore.value ||
            (
                this.productTypeStore.value.abrv !== 'placeholder-(ostomy)' &&
                this.productTypeStore.value.abrv !== 'placeholder-(respiratory)'
            )
            );

        const vendorField = this.form.$('vendorId');
        if (this.showVendor) {
            vendorField.set('rules', 'string|required');
        } else {
            vendorField.set('rules', 'string');
            this.vendorStore.setValue(null);
            this.form.set({ vendorId: null });
            vendorField.showErrors(false);
        }
    }

    @action.bound
    clearProductTagDropdown() {
        this.tagsStore.setValue(null);
        this.form.set({ productTagIds: null });
        this.showProductTags = false;
    }
}

export default ProductEditViewStore;
